import { useState, useEffect, useCallback } from 'react';
import SyncService from '../services/SyncService';

const STORAGE_KEY = 'quran_last_read';

/**
 * Parse JSON safely
 * @param {string} str
 * @param {*} fallback
 * @returns {*}
 */
function safeParse(str, fallback = null) {
  try {
    if (!str) return fallback;
    return JSON.parse(str);
  } catch {
    return fallback;
  }
}

/**
 * Load last read from localStorage
 * @returns {{ surahNumber: number, ayahNumber: number, readingMode: string, translationLang?: string, updatedAt: number } | null}
 */
function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const data = safeParse(raw);
    if (!data || typeof data.surahNumber !== 'number' || typeof data.ayahNumber !== 'number') {
      return null;
    }
    return {
      surahNumber: data.surahNumber,
      ayahNumber: data.ayahNumber,
      readingMode: data.readingMode === 'ayah' ? 'ayah' : 'scroll',
      translationLang: data.translationLang || 'en-urdu',
      contextMode: data.contextMode || 'surah',
      juzNumber: data.juzNumber || null,
      updatedAt: data.updatedAt || 0,
    };
  } catch (err) {
    console.error('useLastRead: Failed to load', err);
    return null;
  }
}

/**
 * useLastRead - Resume Reading / Last Read
 * Persists reading position to localStorage with tab sync.
 *
 * @returns {{ lastRead: object | null, saveLastRead: Function, clearLastRead: Function, hasLastRead: boolean }}
 */
export function useLastRead() {
  const [lastRead, setLastRead] = useState(null);

  useEffect(() => {
    setLastRead(loadFromStorage());
  }, []);

  // Tab sync via storage event
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue !== null) {
        setLastRead(safeParse(e.newValue));
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const saveLastRead = useCallback((surah, ayah, mode, extra = {}) => {
    const payload = {
      surahNumber: Number(surah),
      ayahNumber: Number(ayah),
      readingMode: mode === 'ayah' ? 'ayah' : 'scroll',
      translationLang: extra.translationLang || 'en-urdu',
      contextMode: extra.contextMode || 'surah',
      juzNumber: extra.juzNumber || null,
      updatedAt: Date.now(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      setLastRead(payload);
      SyncService.scheduleSync(); // ← cloud sync trigger
    } catch (err) {
      console.error('useLastRead: Failed to save', err);
    }
  }, []);

  const clearLastRead = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setLastRead(null);
    } catch (err) {
      console.error('useLastRead: Failed to clear', err);
    }
  }, []);

  return {
    lastRead,
    saveLastRead,
    clearLastRead,
    hasLastRead: lastRead !== null,
  };
}
